/** Unidades con valor numérico (API: `{n}m` | `{n}h` | `{n}d` | `{n}a`). */
export type RetentionDurationUnit = 'm' | 'h' | 'd' | 'a';

/** Unidad de UI del selector de retención. */
export type RetentionDurationSelectUnit = 'none' | 'immediate' | RetentionDurationUnit;

/** Valor API: null = no borrar; 0 = al momento; string con sufijo o entero legado (días). */
export type RetentionDurationApiValue = null | number | string;

export interface RetentionDurationParts {
	unit: RetentionDurationSelectUnit;
	amount: number | null;
}

const TIMED_UNITS: readonly RetentionDurationUnit[] = ['m', 'h', 'd', 'a'];
const TIMED_UNIT_SET = new Set<string>(TIMED_UNITS);
const API_VALUE_PATTERN = /^(\d+)([mhda])$/i;

export function isTimedRetentionUnit(unit: RetentionDurationSelectUnit | null | undefined): unit is RetentionDurationUnit {
	return unit != null && TIMED_UNIT_SET.has(unit);
}

/**
 * Parsea el valor API (o legado numérico en días) a unidad + cantidad de UI.
 * Legado: entero positivo sin sufijo → días.
 */
export function parseRetentionDuration(value: RetentionDurationApiValue | number | undefined): RetentionDurationParts {
	if (value === null || value === undefined || value === '') {
		return { unit: 'none', amount: null };
	}

	if (value === 0 || value === '0') {
		return { unit: 'immediate', amount: null };
	}

	if (typeof value === 'number') {
		if (!Number.isFinite(value) || !Number.isInteger(value) || value < 1) {
			return { unit: 'none', amount: null };
		}
		return { unit: 'd', amount: value };
	}

	const trimmed = String(value).trim();
	if (!trimmed) {
		return { unit: 'none', amount: null };
	}

	const match = API_VALUE_PATTERN.exec(trimmed);
	if (match) {
		const amount = Number(match[1]);
		const unit = match[2].toLowerCase() as RetentionDurationUnit;
		if (!Number.isInteger(amount) || amount < 1) {
			return { unit: 'none', amount: null };
		}
		return { unit, amount };
	}

	const asNumber = Number(trimmed);
	if (Number.isInteger(asNumber) && asNumber >= 1) {
		return { unit: 'd', amount: asNumber };
	}

	return { unit: 'none', amount: null };
}

/**
 * Serializa unidad + cantidad al contrato API.
 * none → null; immediate → 0; timed → `{amount}{unit}` (amount ≥ 1).
 */
export function formatRetentionDuration(
	unit: RetentionDurationSelectUnit | null | undefined,
	amount: number | string | null | undefined,
): RetentionDurationApiValue {
	if (!unit || unit === 'none') {
		return null;
	}
	if (unit === 'immediate') {
		return 0;
	}
	if (!isTimedRetentionUnit(unit)) {
		return null;
	}

	const n = typeof amount === 'number' ? amount : Number(amount);
	if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) {
		return null;
	}

	return `${n}${unit}`;
}

/** Clave i18n corta para mostrar el valor en detalle / listados. */
export function retentionDurationDisplayKey(parts: RetentionDurationParts): string {
	switch (parts.unit) {
		case 'none':
			return 'SHARED.RETENTION.NONE';
		case 'immediate':
			return 'SHARED.RETENTION.IMMEDIATE';
		case 'm':
			return 'SHARED.RETENTION.DISPLAY_MINUTES';
		case 'h':
			return 'SHARED.RETENTION.DISPLAY_HOURS';
		case 'd':
			return 'SHARED.RETENTION.DISPLAY_DAYS';
		case 'a':
			return 'SHARED.RETENTION.DISPLAY_YEARS';
		default:
			return 'SHARED.RETENTION.NONE';
	}
}

export const RETENTION_API_FIELD_KEYS = [
	'document_retention_days',
	'media_retention_days',
	'info_retention_days',
] as const;

export type RetentionApiFieldKey = (typeof RETENTION_API_FIELD_KEYS)[number];

export type RetentionApiFields = Partial<Record<RetentionApiFieldKey, RetentionDurationApiValue>>;

/** True si el config/payload trae al menos un valor de retención distinto de null/undefined/''. */
export function hasRetentionOverride(source: RetentionApiFields | null | undefined): boolean {
	if (!source) {
		return false;
	}
	return RETENTION_API_FIELD_KEYS.some((key) => {
		const value = source[key];
		return value !== undefined && value !== null && value !== '';
	});
}

/** «No borrar» → null; resto se deja tal cual (0 o string con sufijo). */
export function normalizeRetentionApiValue(
	value: RetentionDurationApiValue | undefined,
): RetentionDurationApiValue {
	if (value === null || value === undefined || value === '') {
		return null;
	}
	return value;
}

/**
 * Construye el bloque de retención para API.
 * - override off → create omite claves; update envía null en las tres (limpia override).
 * - override on → incluye valores; con omitNullKeys no envía claves en null (create).
 */
export function buildRetentionApiFields(
	values: RetentionApiFields,
	options: { overrideEnabled: boolean; omitNullKeys: boolean },
): RetentionApiFields {
	if (!options.overrideEnabled) {
		if (options.omitNullKeys) {
			return {};
		}
		return {
			document_retention_days: null,
			media_retention_days: null,
			info_retention_days: null,
		};
	}

	const next: RetentionApiFields = {
		document_retention_days: normalizeRetentionApiValue(values.document_retention_days),
		media_retention_days: normalizeRetentionApiValue(values.media_retention_days),
		info_retention_days: normalizeRetentionApiValue(values.info_retention_days),
	};

	if (!options.omitNullKeys) {
		return next;
	}

	const trimmed: RetentionApiFields = {};
	for (const key of RETENTION_API_FIELD_KEYS) {
		if (next[key] !== null && next[key] !== undefined) {
			trimmed[key] = next[key];
		}
	}
	return trimmed;
}

